// Boundary between the Monitoring dashboard API and the dashboard screen.
// GET {domain}monitoring/dashboard/?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
import dayjs from "dayjs";

export const dashboardUrl = (domain, startDate, endDate = startDate) =>
  `${domain}monitoring/dashboard/?start_date=${startDate}&end_date=${endDate}`;

const count = value => Number(value) || 0;

const formatStamp = value => value && dayjs(value).isValid() ? dayjs(value).format("DD MMM, HH:mm") : "—";

// Every secondary counter the API sends for a stage is shown, zero or not:
// "5 retry · 3 quarantined". `alert` parts turn the line amber when non-zero.
const extras = (parts, alert = true) => ({
  extra: parts.map(([value, label]) => `${count(value)} ${label}`).join(" · "),
  extraTone: alert && parts.some(([value]) => count(value) > 0) ? "warning" : "muted"
});

// recent_issues came back empty in the sample response, so its row shape is
// not confirmed yet. These key names are best guesses; adjust once the
// backend returns a populated row.
const mapIssue = (issue = {}, index) => {
  const at = issue.time ?? issue.created_at ?? issue.timestamp ?? issue.event_time;
  return {
    time: at && dayjs(at).isValid() ? dayjs(at).format("HH:mm") : at ?? "—",
    name: issue.employee_name ?? issue.device_name ?? issue.name ?? "—",
    id: String(issue.employee_id ?? issue.device_id ?? issue.id ?? index),
    stage: issue.stage ?? "—",
    status: issue.status ?? issue.issue ?? "—"
  };
};

export function mapDashboard(response, selectedDate = dayjs()) {
  const date = dayjs(selectedDate);
  const periodLabel = date.isSame(dayjs(), "day") ? "today" : `on ${date.format("DD MMM YYYY")}`;
  const data = response || {};
  const events = data.global_events || {};
  const processing = data.processing || {};
  const attendance = data.attendance || {};
  const deviceCounts = data.devices || {};
  const flow = data.processing_flow_health || {};
  const stage = key => flow[key] || {};
  return {
    lastUpdated: data.last_updated || null,
    summary: [{
      title: "Global Events",
      description: "Events received from devices",
      value: count(events.received_today),
      label: `Received ${periodLabel}`,
      rows: [["Processed", count(events.processed), "success"], ["Processing", count(events.processing), "warning"], ["Failed", count(events.failed), "error"]]
    }, {
      title: "Processing",
      description: "Events in processing pipeline",
      value: count(processing.pending_in_queue),
      label: "Pending in queue",
      rows: [["Pending", count(processing.pending), "info"], ["Retry", count(processing.retry), "warning"], ["Quarantined", count(processing.quarantined), "error"]]
    }, {
      title: "Attendance",
      description: "Events processed to attendance",
      value: count(attendance.processed_today),
      label: `Handled ${periodLabel}`,
      // Failed is not shown: the quarantined events are the failed ones.
      rows: [["Completed", count(attendance.completed), "success"], ["Processing", count(attendance.processing), "warning"], ["Quarantined", count(attendance.quarantined), "error"]]
    }, {
      title: "Devices",
      description: "Biometric devices",
      value: count(deviceCounts.total),
      label: "Total devices",
      rows: [["Online", count(deviceCounts.online), "success"], ["No Recent Punches", count(deviceCounts.no_recent_punches), "warning"], ["Offline", count(deviceCounts.offline), "error"]]
    }],
    stages: [{
      name: "Global Event",
      status: stage("global_event").status || "—",
      count: count(stage("global_event").received_today),
      label: `Received ${periodLabel}`,
      ...extras([[stage("global_event").processed, "processed"]], false)
    }, {
      name: "Tenant Targeting",
      status: stage("tenant_targeting").status || "—",
      count: count(stage("tenant_targeting").processed),
      label: "Processed"
    }, {
      name: "Dispatcher",
      status: stage("dispatcher").status || "—",
      count: count(stage("dispatcher").processed),
      label: "Processed",
      ...extras([[stage("dispatcher").retry, "retry"], [stage("dispatcher").quarantined, "quarantined"]])
    }, {
      name: "Punch Entry",
      status: stage("punch_entry").status || "—",
      count: count(stage("punch_entry").processed),
      label: "Processed",
      ...extras([[stage("punch_entry").in_queue, "in queue"]])
    }, {
      name: "Attendance",
      status: stage("attendance").status || "—",
      count: count(stage("attendance").processed),
      label: "Processed",
      ...extras([[stage("attendance").in_queue, "in queue"], [stage("attendance").retry, "retry"], [stage("attendance").quarantined, "quarantined"]])
    }],
    issues: (Array.isArray(data.recent_issues) ? data.recent_issues : []).map(mapIssue),
    jobs: (Array.isArray(data.job_health) ? data.job_health : []).map(job => ({
      name: job.job ?? "—",
      status: job.status ?? "—",
      queue: count(job.current_queue)
    })),
    devices: (Array.isArray(data.device_health) ? data.device_health : []).map(device => ({
      name: device.device_name ?? "—",
      id: String(device.device_id ?? ""),
      status: device.status ?? "—",
      lastSeen: formatStamp(device.last_seen),
      lastPunch: formatStamp(device.last_punch)
    }))
  };
}
